import { FormEvent, useRef, useState } from 'react';
import { useModalFocusTrap } from '../../../accessibility/useModalFocusTrap';
import type { InvestigationContentTypeDto } from '../types/investigation';

const labels: Record<InvestigationContentTypeDto, string> = {
  USER_HYPOTHESIS: 'Add possible cause',
  AI_SUGGESTED_HYPOTHESIS: 'Add Route42-suggested possible cause',
  EVIDENCE_BACKED_FINDING: 'Add what you know',
  CONFLICTING_EVIDENCE: 'Record conflicting evidence',
  REJECTED_HYPOTHESIS: 'Rule out possible cause',
  USER_NOTE: 'Add note',
  OPEN_QUESTION: 'Add something to check',
  FINAL_CONCLUSION: 'Update where things stand',
};

const fieldLabels: Record<InvestigationContentTypeDto, string> = {
  USER_HYPOTHESIS: 'Possible cause',
  AI_SUGGESTED_HYPOTHESIS: 'Possible cause',
  EVIDENCE_BACKED_FINDING: 'What you know',
  CONFLICTING_EVIDENCE: 'Conflicting evidence',
  REJECTED_HYPOTHESIS: 'Possible cause to rule out',
  USER_NOTE: 'Note',
  OPEN_QUESTION: 'What needs checking',
  FINAL_CONCLUSION: 'Where things stand',
};

const helpers: Record<InvestigationContentTypeDto, string> = {
  USER_HYPOTHESIS: 'Add something that might explain the problem but is not confirmed yet.',
  AI_SUGGESTED_HYPOTHESIS: 'Record a Route42-suggested possible cause that still needs evidence before it can be confirmed.',
  EVIDENCE_BACKED_FINDING: 'Add a fact, measurement, observation, or conclusion your team can support.',
  CONFLICTING_EVIDENCE: 'Record evidence that does not agree cleanly with the current understanding.',
  REJECTED_HYPOTHESIS: 'Record a possible cause the team has enough evidence to rule out.',
  USER_NOTE: 'Add useful context that should stay with this Investigation.',
  OPEN_QUESTION: "Add a question, measurement, inspection, or evidence check that could change the team's understanding.",
  FINAL_CONCLUSION: "Summarize the team's current best-supported understanding, including important uncertainty.",
};

export function AddInvestigationContentDialog({ type, busy, error, onClose, onSubmit }: {
  type: InvestigationContentTypeDto; busy: boolean; error: string | null; onClose: () => void; onSubmit: (text: string) => Promise<void> | void;
}) {
  const [text, setText] = useState('');
  const dialogRef = useRef<HTMLFormElement>(null);
  useModalFocusTrap(dialogRef, true, busy ? undefined : onClose);
  function submit(event: FormEvent) { event.preventDefault(); if (text.trim() && !busy) void onSubmit(text.trim()); }
  return <div className="r42-dialog-layer" role="dialog" aria-modal="true" aria-labelledby="investigation-content-title" aria-describedby="investigation-content-description">
    <button className="r42-dialog-backdrop" type="button" tabIndex={-1} aria-label="Close" onClick={onClose} disabled={busy} />
    <form ref={dialogRef} tabIndex={-1} className="r42-dialog-card" onSubmit={submit}>
      <span className="r42-eyebrow">Investigation update</span><h2 id="investigation-content-title">{labels[type]}</h2>
      <p id="investigation-content-description">{helpers[type]} This stays with this Investigation and does not rewrite Machine Memory. After you save, Route42 reviews whether this changes the shared understanding.</p>
      {error ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" /><div><strong>Route42 couldn't save this update.</strong><span>{error}</span></div></div> : null}
      <label className="r42-field"><span className="r42-field__label">{fieldLabels[type]} <b>Required</b></span><textarea data-autofocus="true" rows={5} maxLength={4000} value={text} onChange={(event) => setText(event.target.value)} /></label>
      <div className="r42-dialog-actions"><button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button><button className="btn r42-btn-primary" type="submit" disabled={busy || !text.trim()}>{busy ? 'Saving & reviewing…' : 'Save update'}</button></div>
    </form>
  </div>;
}
